<script setup>
import {ref, watch} from "vue";
import {useUnsavedChanges, unsavedChangesSnapshot} from '@/composables/useUnsavedChanges.js';
import {
    MailIcon,
    PhoneIcon,
    GlobeAltIcon,
} from '@heroicons/vue/outline';

const props = defineProps({
    leadId: {
        type: Number,
        required: true,
    },
    allowDelete: {
        type: Boolean,
        required: false,
        default: true
    },
    showActionsButton: {
        type: Boolean,
        required: false,
        default: true,
    },
    allowArchive: {
        type: Boolean,
        required: false,
        default: false,
    },
    redirectAfterDelete: {
        type: Boolean,
        required: false,
        default: true,
    }
})

const emit = defineEmits(['update:lead', 'reload:lead', 'archived', 'deleted'])
const lead = ref(null)
const workingState = ref('')
let savedSnapshot = null

useUnsavedChanges(() => lead.value !== null && unsavedChangesSnapshot(lead.value) !== savedSnapshot)

const loadLead = async () => {
    const res = await axios.get(route('bo.api.v1.crm.lead.show', props.leadId))
    lead.value = res.data.lead
    savedSnapshot = unsavedChangesSnapshot(lead.value)
}

const updateLead = async () => {
    if (! lead.value.name) {
        return;
    }

    workingState.value = 'working'
    try {
        const res = await axios.put(route('api.crm.lead.update', props.leadId), lead.value)
        workingState.value = 'success'
        lead.value = res.data.lead
        savedSnapshot = unsavedChangesSnapshot(lead.value)
        emit('update:lead', lead.value)
    } catch (error) {
        workingState.value = 'error'
    }
}

watch(() => props.leadId, () => {
    if (props.leadId) {
        loadLead()
    }
}, {immediate: true})

</script>

<template>
    <div v-if="lead" class="@container">
        <div class="grid-cols-4 gap-x-8 md:grid @sm:grid-cols-2 @lg:grid-cols-4">
            <div class="col-span-2">
                <input-text
                    auto-complete="off"
                    maxlength="255"
                    name="name"
                    input-id="name"
                    :label="_mft('crm:contact.fields.name')"
                    :required="true"
                    v-model:content="lead.name"
                />

                <div class="flex items-center space-x-2">
                    <input-text
                        auto-complete="off"
                        maxlength="255"
                        name="email"
                        input-id="email"
                        class="w-full"
                        :label="_mft('crm:contact.fields.email')"
                        v-model:content="lead.email"
                    />
                    <a
                        v-if="lead.email?.length > 5"
                        :href="'mailto:' + lead.email"
                    >
                        <MailIcon class="w-6 h-6 mt-2 text-gray-400" />
                    </a>
                </div>

                <input-text
                    auto-complete="off"
                    maxlength="255"
                    name="title"
                    input-id="title"
                    :label="_mft('crm:contact.fields.title')"
                    v-model:content="lead.title"
                />
            </div>
            <div class="col-span-2">
                <div class="flex items-center space-x-2">
                    <input-text
                        auto-complete="off"
                        maxlength="255"
                        name="phone"
                        input-id="phone"
                        class="w-full"
                        :label="_mft('crm:contact.fields.phone')"
                        v-model:content="lead.phone"
                    />
                    <a
                        v-if="lead.phone?.length > 3"
                        :href="'tel:' + lead.phone.replace(/[^0-9]/g, '')"
                    >
                        <PhoneIcon class="w-6 h-6 mt-2 text-gray-400" />
                    </a>
                </div>

                <div class="flex items-center space-x-2">
                    <input-text
                        auto-complete="off"
                        maxlength="255"
                        name="linkedin"
                        input-id="linkedin"
                        class="w-full"
                        :label="_mft('crm:contact.fields.linkedIn')"
                        v-model:content="lead.linkedin"
                    />
                    <a
                        v-if="lead.linkedin?.length > 12"
                        :href="lead.linkedin"
                        target="_blank"
                    >
                        <i class="w-6 h-6 mt-2 text-2xl text-gray-400 fa-brands fa-linkedin"></i>
                    </a>
                </div>

                <div class="flex items-center space-x-2">
                    <input-text
                        auto-complete="off"
                        maxlength="255"
                        name="website"
                        input-id="website"
                        class="w-full"
                        :label="_mft('crm:contact.company.fields.website')"
                        v-model:content="lead.website"
                    />
                    <a
                        v-if="lead.website?.length > 11"
                        target="_blank"
                        :href="lead.website"
                    >
                        <GlobeAltIcon class="w-6 h-6 mt-2 text-gray-400" />
                    </a>
                </div>
            </div>
            <crm-lead-custom-fields
                :lead="lead"
            />
        </div>
        <div class="flex justify-end space-x-2">
            <lead-actions-button
                v-if="showActionsButton"
                :lead="lead"
                :allow-delete="allowDelete"
                :allow-archive="allowArchive"
                :redirect-after-delete="redirectAfterDelete"
                @reload:lead="emit('reload:lead')"
                @archived="emit('archived')"
                @deleted="emit('deleted')"
            />

            <button-base
                :style-type="lead.name ? 'success' : 'disabled'"
                :disabled="!lead.name || workingState === 'working'"
                @click="updateLead"
                :state="workingState"
            >
                {{ _mft('shared:action.update') }}
            </button-base>
        </div>
    </div>
</template>

<style scoped>

</style>
